import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  WritableSignal,
  computed,
  effect,
  inject,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  bufferTime,
  debounceTime,
  filter,
  map,
  switchMap,
  take,
  timer,
} from 'rxjs';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { injectVirtualizer } from '@tanstack/angular-virtual';
import {
  PreloaderColor,
  UiCameraSelectButtonComponent,
  UiCalendarComponent,
  UiFilterChipComponent,
  UiPreloaderComponent,
  OnRangeSelectedResult,
} from '@ui';
import { analyticClassName } from '@consts/alert-events.const';
import { routerSegments } from '@consts/routes';
import { AlertEventsService } from '../../../../../../development/alert-events.service';
import { AlertEntry } from '../../../../../../development/alerts.service';
import { UtilsService } from '../../../../../../edge/utils.service';
import { CameraPickerModalComponent } from '../../../../../../modals/camera-picker-modal/camera-picker-modal.component';
import { CameraPickerModels } from '../../../../../../modals/camera-picker-inline/models/models';
import { StepOptionSelectorModule } from '../../../../../../pages/alerts-v2/components/step-option-selector/step-option-selector.module';
import { AlertPreviewComponent } from '../../../../../../pages/alerts-v2/components/alert-preview/alert-preview.component';
import { TagFilterComponent } from '../../../../../../pages/database/pages/tags/components/tag-filter/tag-filter.component';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import { IntelligenceToolsService } from '../../../../../../services/intelligence-tools.service';
import { UtilsV2Service } from '../../../../../../services/utils-v2.service';
import { AppState } from '../../../../../../store/app.state';
import { AlertCategory, EventLookupItem, FlowLookup, SystemType } from '@models/alerts-v2.model';
import { IntelligenceModels } from '@models/intelligence.model';
import { AlertType, AnalyticClasses, DetectionType } from '@enums/alert-events.enum';
import { UIInputStyle, UiCalendarPickerType } from '@enums/shared.enum';
import { UiV2SelectorComponent } from '@ui-kit/ui-v2-selector/ui-v2-selector.component';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { CameraStatusSelectors } from '@states/camera-status/camera-status.selector-types';
import { DatabaseTagsActions } from '@states/database-tags/database-tags.action.types';
import { DatabaseTagsSelectors } from '@states/database-tags/database-tags.selector-types';
import { LocationSelectors } from '@states/location/location.selector-types';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { EdgeSelectors } from '@states/edge/edge.selector-types';
import { SharedActions } from '@states/shared/shared.action-types';
import { selectUserHoursFormat } from '@states/user-settings/user-settings.selectors';
import {
  buildAlertTypeGroupsForSelector,
  buildAlertTypeKeyValuePairs,
  fromFlowTypeKey,
  toFlowTypeKey,
} from '../alert-type-filter.util';
import {
  filterIncomingLiveAlerts,
  relativeRangeToMs,
  toLiveAlertItem,
} from '../alert-monitoring-live.util';
import { SocketMainService } from '../../../../../../socket/socket-main.service';
import { SocketEvents } from '../../../../../../socket/socket.model';

const ESTIMATED_ROW_HEIGHT = 250;
const NARROW_CONTAINER_WIDTH = 680;
const OVERSCAN_ROWS = 3;
const LOAD_MORE_ROW_THRESHOLD = 4;
const FILTER_DEBOUNCE_MS = 300;
const ALERT_SOCKET_BUFFER_MS = 1000;
const SOCKET_CONNECT_RETRY_MS = 2000;
const DEFAULT_LIVE_HOURS_BACK = 24;
const NEW_ALERT_AUDIO_SRC = '../assets/audio/new-alert.mp3';
const ACKNOWLEDGE_OPTIONS: IntelligenceModels.Alerts.AcknowledgeOption[] = [
  { label: 'ACKNOWLEDGE_FILTER.ACKNOWLEDGES', value: true },
  { label: 'ACKNOWLEDGE_FILTER.UNACKNOWLEDGES', value: false },
];

@Component({
  selector: 'app-alert-monitoring-result',
  standalone: true,
  imports: [
    AlertPreviewComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatMenuModule,
    MatSelectModule,
    MatTooltip,
    TranslatePipe,
    UiCameraSelectButtonComponent,
    UiCalendarComponent,
    UiFilterChipComponent,
    UiPreloaderComponent,
    UiV2SelectorComponent,
    StepOptionSelectorModule,
    TagFilterComponent,
  ],
  templateUrl: './alert-monitoring-result.component.html',
  styleUrls: ['./alert-monitoring-result.component.scss'],
})
export class AlertMonitoringResultComponent
  implements AfterViewInit, OnDestroy, OnInit
{
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly toolsService = inject(IntelligenceToolsService);
  private readonly utilsService = inject(UtilsService);
  private readonly utilsV2Service = inject(UtilsV2Service);
  private readonly router = inject(Router);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly translateService = inject(TranslateService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly alertEventsService = inject(AlertEventsService);
  private readonly socketMainService = inject(SocketMainService);
  private resizeObserver: ResizeObserver | null = null;
  private lastEntryPosition: number = this.resultsService.currentPosition();
  private filterRequestId = 0;
  private pendingLiveAlerts: AlertEntry[] = [];
  private audio: HTMLAudioElement | null = null;

  private readonly listWrapper = viewChild<ElementRef<HTMLElement>>('listWrapper');
  private readonly virtualRows = viewChildren<ElementRef<HTMLElement>>('virtualRow');

  protected readonly PreloaderColor = PreloaderColor;
  protected readonly AnalyticClasses = AnalyticClasses;
  protected readonly inputStyles: typeof UIInputStyle = UIInputStyle;
  protected readonly analyticClassName = analyticClassName;
  protected readonly acknowledgeOptions = ACKNOWLEDGE_OPTIONS;
  protected readonly columns = signal<number>(2);
  protected readonly scrollParent = signal<HTMLElement | null>(null);
  protected readonly listOffset = signal<number>(0);
  protected readonly isLoading = signal<boolean>(false);
  protected readonly isFiltering = signal<boolean>(false);
  protected readonly muted = signal<boolean>(true);
  protected readonly selectedEventIds = signal<string[]>([]);
  protected readonly selectedCameras = signal<
    Dictionary<CameraPickerModels.SelectedCamera>
  >({});
  protected readonly selectedTrackedObjects = signal<AnalyticClasses[]>([]);
  protected readonly selectedFlowTypes = signal<FlowLookup[]>([]);
  protected readonly selectedTags = signal<string[]>([]);
  protected readonly selectedAcknowledges = signal<boolean[]>([]);
  protected readonly dateRange: WritableSignal<OnRangeSelectedResult | null> =
    signal<OnRangeSelectedResult | null>(null);
  protected readonly eventLookupOptions = signal<EventLookupItem[]>([]);
  protected readonly eventSelectorControl = new FormControl<string[]>([], {
    nonNullable: true,
  });
  protected readonly tagsFilterControl = new FormControl<string[]>([], {
    nonNullable: true,
  });

  protected readonly cameraLookup = this.store.selectSignal(
    CameraSelectors.selectCamerasLookup,
  );
  protected readonly orgCameraIds = this.store.selectSignal(
    CameraSelectors.selectAllCamerasIds,
  );
  protected readonly orgEdgeIds = this.store.selectSignal(
    EdgeSelectors.selectAllEdgesIds,
  );
  protected readonly cameraStatuses = this.store.selectSignal(
    CameraStatusSelectors.selectAllDisplayCameraStatus,
  );
  protected readonly locationLookup = this.store.selectSignal(
    LocationSelectors.selectLocationEntities,
  );
  protected readonly tagEntities = this.store.selectSignal(
    DatabaseTagsSelectors.selectDatabaseTagEntities,
  );
  protected readonly maxRetentionDays = this.store.selectSignal(
    OrganizationSelectors.selectMaxRetentionDays,
  );
  protected readonly hoursFormat = this.store.selectSignal(selectUserHoursFormat);
  private readonly enablePalestinianLicensePlate = this.store.selectSignal(
    OrganizationSelectors.selectEnablePalestinianLicensePlate,
  );
  private readonly disableMissingObjectEventConfiguration = this.store.selectSignal(
    OrganizationSelectors.selectDisableMissingObjectEventConfiguration,
  );
  private readonly isDeveloper = this.store.selectSignal(OrganizationSelectors.isDeveloper);

  protected readonly alertTypeGroupsForSelector = computed(() =>
    buildAlertTypeGroupsForSelector(
      {
        enablePalestinianLicensePlate: this.enablePalestinianLicensePlate(),
        disableMissingObjectEventConfiguration: this.disableMissingObjectEventConfiguration(),
      },
      this.isDeveloper(),
    ),
  );
  protected readonly alertTypeKeyValuePairs = computed(() =>
    buildAlertTypeKeyValuePairs(
      {
        enablePalestinianLicensePlate: this.enablePalestinianLicensePlate(),
        disableMissingObjectEventConfiguration: this.disableMissingObjectEventConfiguration(),
      },
      this.isDeveloper(),
    ),
  );
  protected readonly selectedFlowTypeKeys = computed<string[]>(() =>
    this.selectedFlowTypes().map((flowType) =>
      toFlowTypeKey(flowType.category, flowType.flowType),
    ),
  );
  protected readonly tagToEventIds = computed<Record<string, string[]>>(() => {
    const tagToEventIds: Record<string, string[]> = {};
    this.eventLookupOptions().forEach((event) => {
      (event.tags ?? []).forEach((tag) => {
        (tagToEventIds[tag] ??= []).push(event._id);
      });
    });
    return tagToEventIds;
  });
  protected readonly selectedCameraList = computed<
    CameraPickerModels.SelectedCamera[]
  >(() =>
    Object.values(this.selectedCameras()).filter(
      (camera): camera is CameraPickerModels.SelectedCamera => !!camera,
    ),
  );
  protected readonly hasActiveFilters = computed<boolean>(
    () =>
      this.selectedEventIds().length > 0 ||
      this.selectedCameraList().length > 0 ||
      this.selectedTrackedObjects().length > 0 ||
      this.selectedFlowTypes().length > 0 ||
      this.selectedTags().length > 0 ||
      this.selectedAcknowledges().length > 0 ||
      this.dateRange() !== null,
  );

  protected readonly response = computed<IntelligenceModels.Alerts.Response | null>(
    () => {
      const result = this.resultsService.current();
      if (result?.type !== 'alerts') return null;
      return result.data as IntelligenceModels.Alerts.Response;
    },
  );
  protected readonly calendarRange = computed<OnRangeSelectedResult | null>(() => {
    const selected = this.dateRange();
    if (selected) return selected;
    const filter = this.response()?.filter;
    if (!filter?.resolvedStartTime || !filter?.resolvedEndTime) return null;
    return {
      type: UiCalendarPickerType.ABSOLUTE,
      relative: null,
      absolute: { start: filter.resolvedStartTime, end: filter.resolvedEndTime },
    };
  });
  protected readonly alerts = computed<AlertEntry[]>(() =>
    (this.response()?.results ?? []).map(
      (item) => item.monitoringAlert ?? this.fromLegacyResult(item),
    ),
  );
  protected readonly totalCount = computed<number>(
    () => this.response()?.totalCount ?? 0,
  );
  protected readonly topCameras = computed<IntelligenceModels.Alerts.BreakdownItem[]>(
    () => this.response()?.topN?.cameras ?? [],
  );
  protected readonly topEventTypes = computed<
    IntelligenceModels.Alerts.BreakdownItem[]
  >(() => this.response()?.topN?.eventTypes ?? []);
  protected readonly rows = computed<AlertEntry[][]>(() =>
    this.chunk(this.alerts(), this.columns()),
  );
  protected readonly virtualizationEnabled = computed<boolean>(
    () => this.scrollParent() !== null,
  );
  protected readonly hasMore = computed<boolean>(
    () => this.response()?.hasMore ?? false,
  );

  protected readonly virtualizer = injectVirtualizer<HTMLElement, HTMLElement>(
    () => ({
      scrollElement: this.scrollParent() ?? undefined,
      count: this.rows().length,
      estimateSize: () => ESTIMATED_ROW_HEIGHT,
      overscan: OVERSCAN_ROWS,
      scrollMargin: this.listOffset(),
      getItemKey: (index: number) =>
        `${this.firstAlertIdOfRow(this.rows()[index])}-${this.columns()}`,
    }),
  );

  protected readonly eventChips = computed<
    Array<{ label: string; count: number; colorClass: string }>
  >(() =>
    this.topEventTypes()
      .slice(0, 4)
      .map((eventType) => ({
        label: eventType.eventName ?? '',
        count: eventType.count,
        colorClass: this.eventColorClass(eventType.eventName),
      })),
  );

  private readonly resetOnNavigation = effect(() => {
    const position = this.resultsService.currentPosition();
    if (position === this.lastEntryPosition) return;
    this.lastEntryPosition = position;
    this.resetFilters();
  });
  private readonly updateListOffsetEffect = effect(() => {
    this.response();
    this.columns();
    this.listWrapper();
    this.updateListOffset();
  });
  private readonly measureVirtualRowsEffect = effect(() => {
    this.virtualRows().forEach((row) => {
      this.virtualizer.measureElement(row.nativeElement);
    });
  });
  private readonly loadMoreEffect = effect(() => {
    const virtualItems = this.virtualizer.getVirtualItems();
    const lastItem = virtualItems[virtualItems.length - 1];
    if (
      lastItem &&
      lastItem.index >= this.rows().length - LOAD_MORE_ROW_THRESHOLD
    ) {
      void this.loadMore();
    }
  });

  public ngOnInit(): void {
    if (!Object.keys(this.tagEntities()).length) {
      this.store.dispatch(DatabaseTagsActions.getDatabaseTags());
    }
    this.alertEventsService
      .getEventLookup('')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options: EventLookupItem[]) => {
        this.eventLookupOptions.set(options);
      });
    this.restoreUiState();
    this.eventSelectorControl.valueChanges
      .pipe(debounceTime(FILTER_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef))
      .subscribe((eventIds: string[]) => {
        this.selectedEventIds.set(eventIds);
        void this.reload();
      });
    this.tagsFilterControl.valueChanges
      .pipe(debounceTime(FILTER_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef))
      .subscribe((tagIds: string[]) => {
        this.selectedTags.set(tagIds);
        void this.reload();
      });
    this.ngZone.runOutsideAngular(() => {
      timer(0, SOCKET_CONNECT_RETRY_MS)
        .pipe(
          filter(() => this.socketMainService.isValid()),
          take(1),
          switchMap(() =>
            this.socketMainService.consume<{ result: AlertEntry[] }>(
              SocketEvents.edgeIncomingAnalyticAlerts,
            ),
          ),
          bufferTime(ALERT_SOCKET_BUFFER_MS),
          map((buffered) => buffered.flatMap((event) => event.result ?? [])),
          filter((alerts) => alerts.length > 0),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((alerts) =>
          this.ngZone.run(() => this.onIncomingLiveAlerts(alerts)),
        );
    });
  }

  public ngAfterViewInit(): void {
    this.updateColumns(this.host.nativeElement.clientWidth);
    this.scrollParent.set(this.findScrollParent(this.host.nativeElement));
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        const entry = entries[0];
        if (!entry) return;
        this.updateColumns(entry.contentRect.width);
        this.updateListOffset();
      });
      this.resizeObserver.observe(this.host.nativeElement);
    }
    queueMicrotask(() => this.updateListOffset());
  }

  public ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  protected openSelectCameraModal(): void {
    const data: CameraPickerModels.CameraPickerDialogOptions = {
      options: { multi: true },
      selectedValue: this.selectedCameras(),
    };
    this.dialog
      .open(CameraPickerModalComponent, {
        width: '600px',
        height: '70vh',
        panelClass: 'modal-no-padding',
        disableClose: true,
        data,
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cameras?: Dictionary<CameraPickerModels.SelectedCamera>) => {
        if (!cameras) return;
        this.selectedCameras.set(cameras);
        void this.reload();
      });
  }

  protected onTrackedObjectsChange(trackedObjects: AnalyticClasses[]): void {
    this.selectedTrackedObjects.set(trackedObjects ?? []);
    void this.reload();
  }

  protected onFlowTypesKeysChange(keys: string[]): void {
    this.selectedFlowTypes.set(
      (keys ?? [])
        .map((key) => fromFlowTypeKey(key))
        .filter((flowType): flowType is FlowLookup => !!flowType),
    );
    void this.reload();
  }

  protected onAcknowledgesChange(acknowledges: boolean[]): void {
    this.selectedAcknowledges.set(acknowledges ?? []);
    void this.reload();
  }

  protected changeDateRange(event: OnRangeSelectedResult): void {
    if (!event) return;
    this.dateRange.set(event);
    void this.reload();
  }

  protected onRemoveEvent(eventId: string): void {
    this.eventSelectorControl.setValue(
      this.selectedEventIds().filter((selectedId) => selectedId !== eventId),
    );
  }

  protected onRemoveCamera(cameraId: string): void {
    this.selectedCameras.update((cameras) => {
      const next = { ...cameras };
      delete next[cameraId];
      return next;
    });
    void this.reload();
  }

  protected onRemoveTrackedObject(trackedObject: AnalyticClasses): void {
    this.selectedTrackedObjects.update((selected) =>
      selected.filter((value) => value !== trackedObject),
    );
    void this.reload();
  }

  protected onRemoveFlowType(flowType: FlowLookup): void {
    this.selectedFlowTypes.update((selected) =>
      selected.filter(
        (value) =>
          value.category !== flowType.category ||
          value.flowType !== flowType.flowType,
      ),
    );
    void this.reload();
  }

  protected onRemoveTag(tagId: string): void {
    this.tagsFilterControl.setValue(
      this.selectedTags().filter((selectedId) => selectedId !== tagId),
    );
  }

  protected onRemoveAcknowledge(acknowledge: boolean): void {
    this.selectedAcknowledges.update((selected) =>
      selected.filter((value) => value !== acknowledge),
    );
    void this.reload();
  }

  protected flowName(category: number, flowType: number): string | null {
    return this.alertTypeKeyValuePairs()[toFlowTypeKey(category, flowType)] ?? null;
  }

  protected eventName(eventId: string): string | undefined {
    return this.eventLookupOptions().find((event) => event._id === eventId)
      ?.name;
  }

  protected eventColorClass(name?: string): string {
    if (!name) return 'event--default';
    const normalizedName = name.toLowerCase();
    if (
      normalizedName.includes('fire') ||
      normalizedName.includes('flame') ||
      normalizedName.includes('smoke')
    ) {
      return 'event--fire';
    }
    if (
      normalizedName.includes('suspect') ||
      normalizedName.includes('intru') ||
      normalizedName.includes('breach')
    ) {
      return 'event--suspect';
    }
    if (
      normalizedName.includes('face') ||
      normalizedName.includes('person') ||
      normalizedName.includes('people')
    ) {
      return 'event--face';
    }
    if (
      normalizedName.includes('vehicle') ||
      normalizedName.includes('car') ||
      normalizedName.includes('truck')
    ) {
      return 'event--vehicle';
    }
    if (normalizedName.includes('loiter') || normalizedName.includes('crowd')) {
      return 'event--warning';
    }
    return 'event--default';
  }

  protected trackByAlert(index: number, alert: AlertEntry): string {
    return (
      alert.alertInstanceId?.toString() ?? alert._id ?? `${alert.timestamp}-${index}`
    );
  }

  protected openAlertMonitoringPage(): void {
    const tree = this.router.createUrlTree([
      '/',
      routerSegments.alertsV2,
      routerSegments.monitoring,
    ]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin
      ? `${window.location.origin}${path}`
      : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  protected openAlertTemplates(): void {
    if (!this.response()) return;
    this.resultsService.setOverlay(
      { template: null },
      'alert-templates',
      this.translateService.instant(
        'SEARCH_GLOBAL_MODAL.ALERT_CONFIGURATIONS.CREATE_ALERT',
      ),
    );
  }

  protected toggleMute(): void {
    this.muted.update((muted) => !muted);
  }

  protected async reload(): Promise<void> {
    const filter = this.response()?.filter;
    if (!filter) return;
    const requestId = ++this.filterRequestId;
    const entryPosition = this.resultsService.currentPosition();
    let startTime = filter.resolvedStartTime ?? filter.startTime;
    let endTime = filter.resolvedEndTime ?? filter.endTime;
    const selectedRange = this.dateRange();
    if (selectedRange) {
      const { start, end } = this.utilsV2Service.dateRangeToServerRequest(
        selectedRange,
      );
      startTime = new Date(start).toISOString();
      endTime = new Date(end).toISOString();
    }
    const params: IntelligenceModels.Alerts.ListParams = {
      startTime,
      endTime,
      cameraIds: this.selectedCameraList().map((camera) => camera.cameraId),
      size: filter.size ?? IntelligenceModels.Alerts.DEFAULT_LIMIT,
      eventIds: this.selectedEventIds().length
        ? this.selectedEventIds()
        : undefined,
      tagIds: this.selectedTags().length ? this.selectedTags() : undefined,
      trackedObjects: this.selectedTrackedObjects().length
        ? this.selectedTrackedObjects()
        : undefined,
      acknowledges: this.selectedAcknowledges().length
        ? this.selectedAcknowledges()
        : undefined,
      flowTypes: this.selectedFlowTypes().length
        ? this.selectedFlowTypes()
        : undefined,
    };
    this.isFiltering.set(true);
    try {
      const response = await this.toolsService.fetchAlerts(params);
      if (
        requestId !== this.filterRequestId ||
        entryPosition !== this.resultsService.currentPosition()
      ) {
        return;
      }
      this.resultsService.replaceAlertsInCurrent({
        ...response,
        uiState: this.captureUiState(),
      });
    } catch (err) {
      if (requestId === this.filterRequestId) {
        this.store.dispatch(
          SharedActions.showMessage({
            error: this.utilsService.errMessage(err as Error),
          }),
        );
      }
    } finally {
      if (requestId === this.filterRequestId) {
        this.isFiltering.set(false);
        this.flushPendingLiveAlerts();
      }
    }
  }

  protected async loadMore(): Promise<void> {
    if (this.isLoading() || !this.hasMore()) return;
    const filter = this.response()?.filter;
    const alerts = this.alerts();
    if (!filter || !alerts.length) return;
    const cursor = alerts[alerts.length - 1].timestamp;
    if (!cursor) return;
    const size = filter.size ?? IntelligenceModels.Alerts.DEFAULT_LIMIT;
    const requestId = this.filterRequestId;
    const entryPosition = this.resultsService.currentPosition();
    const params: IntelligenceModels.Alerts.ListParams = {
      startTime: filter.resolvedStartTime ?? filter.startTime,
      endTime: filter.resolvedEndTime ?? filter.endTime,
      cameraIds: filter.cameraIds,
      cameraNames: filter.cameraNames,
      size,
      latestTs: cursor,
      eventIds: filter.eventIds,
      tagIds: filter.tagIds,
      trackedObjects: filter.trackedObjects,
      acknowledges: filter.acknowledges,
      flowTypes: filter.flowTypes,
    };
    this.isLoading.set(true);
    try {
      const page = await this.toolsService.fetchAlerts(params);
      if (
        requestId !== this.filterRequestId ||
        entryPosition !== this.resultsService.currentPosition()
      ) {
        return;
      }
      const hasMore =
        (page.results?.length ?? 0) === size && page.hasMore !== false;
      this.resultsService.appendAlertsToCurrent(page.results ?? [], cursor, hasMore);
    } catch (err) {
      this.store.dispatch(
        SharedActions.showMessage({
          error: this.utilsService.errMessage(err as Error),
        }),
      );
    } finally {
      this.isLoading.set(false);
      this.flushPendingLiveAlerts();
    }
  }

  private resetFilters(): void {
    this.filterRequestId++;
    this.isFiltering.set(false);
    this.pendingLiveAlerts = [];
    this.selectedEventIds.set([]);
    this.eventSelectorControl.setValue([], { emitEvent: false });
    this.selectedCameras.set({});
    this.selectedTrackedObjects.set([]);
    this.selectedFlowTypes.set([]);
    this.selectedTags.set([]);
    this.tagsFilterControl.setValue([], { emitEvent: false });
    this.selectedAcknowledges.set([]);
    this.dateRange.set(null);
  }

  private captureUiState(): IntelligenceModels.Alerts.UiState {
    return {
      eventIds: this.selectedEventIds(),
      cameras: this.selectedCameras(),
      trackedObjects: this.selectedTrackedObjects(),
      flowTypes: this.selectedFlowTypes(),
      tagIds: this.selectedTags(),
      acknowledges: this.selectedAcknowledges(),
      dateRange: this.dateRange() ?? undefined,
    };
  }

  private restoreUiState(): void {
    const state = this.response()?.uiState;
    if (state) {
      this.selectedEventIds.set(state.eventIds);
      this.eventSelectorControl.setValue(state.eventIds, { emitEvent: false });
      this.selectedCameras.set(
        state.cameras as Dictionary<CameraPickerModels.SelectedCamera>,
      );
      this.selectedTrackedObjects.set(state.trackedObjects);
      this.selectedFlowTypes.set(state.flowTypes);
      this.selectedTags.set(state.tagIds);
      this.tagsFilterControl.setValue(state.tagIds, { emitEvent: false });
      this.selectedAcknowledges.set(state.acknowledges);
      this.dateRange.set(state.dateRange ?? null);
      return;
    }
    const cameraIds = this.response()?.filter?.cameraIds ?? [];
    this.selectedCameras.set(
      cameraIds.reduce<Dictionary<CameraPickerModels.SelectedCamera>>(
        (cameras, cameraId) => {
          const cameraItem = this.cameraLookup()[cameraId];
          return {
            ...cameras,
            [cameraId]: cameraItem
              ? {
                  ...CameraPickerModels.transformEdgeItemCameraToSelectedCamera(cameraItem),
                  cameraId,
                }
              : ({ cameraId } as CameraPickerModels.SelectedCamera),
          };
        },
        {},
      ),
    );
  }

  private onIncomingLiveAlerts(alerts: AlertEntry[]): void {
    if (this.isFiltering() || this.isLoading()) {
      this.pendingLiveAlerts.push(...alerts);
      return;
    }
    if (!this.response()?.success) return;
    const cutoffTs = this.resolveLiveCutoff();
    if (cutoffTs === null) return;

    const acceptedAlerts = filterIncomingLiveAlerts(alerts, {
      cutoffTs,
      orgCameraIds: this.orgCameraIds().map(String),
      orgEdgeIds: this.orgEdgeIds().map(String),
      eventIds: this.selectedEventIds(),
      tagIds: this.selectedTags(),
      tagToEventIds: this.tagToEventIds(),
      cameraIds: this.selectedCameraList().map((camera) => camera.cameraId),
      trackedObjects: this.selectedTrackedObjects(),
      flowTypes: this.selectedFlowTypes(),
      acknowledges: this.selectedAcknowledges(),
    }).sort((left, right) => right.timestamp - left.timestamp);
    const prepended = this.resultsService.prependAlertsToCurrent(
      acceptedAlerts.map((alert) => toLiveAlertItem(alert)),
    );
    if (prepended > 0 && !this.muted()) this.playNewAlertSound();
  }

  private resolveLiveCutoff(): number | null {
    if (
      this.selectedAcknowledges().length === 1 &&
      this.selectedAcknowledges()[0] === true
    ) {
      return null;
    }
    const selectedRange = this.dateRange();
    if (selectedRange) {
      if (
        selectedRange.type !== UiCalendarPickerType.RELATIVE ||
        selectedRange.relative?.value == null
      ) {
        return null;
      }
      return Date.now() - relativeRangeToMs(selectedRange.relative);
    }
    const filter = this.response()?.filter;
    if (filter?.startTime && filter.endTime) return null;
    return Date.now() - (filter?.hoursBack ?? DEFAULT_LIVE_HOURS_BACK) * 60 * 60 * 1000;
  }

  private flushPendingLiveAlerts(): void {
    const pendingLiveAlerts = this.pendingLiveAlerts;
    this.pendingLiveAlerts = [];
    if (pendingLiveAlerts.length) this.onIncomingLiveAlerts(pendingLiveAlerts);
  }

  private playNewAlertSound(): void {
    this.audio ??= new Audio(NEW_ALERT_AUDIO_SRC);
    this.audio.load();
    void this.audio.play().catch(() => undefined);
  }

  private chunk(alerts: AlertEntry[], columns: number): AlertEntry[][] {
    const rows: AlertEntry[][] = [];
    for (let index = 0; index < alerts.length; index += columns) {
      rows.push(alerts.slice(index, index + columns));
    }
    return rows;
  }

  private firstAlertIdOfRow(row?: AlertEntry[]): string {
    const alert = row?.[0];
    return (
      alert?.alertInstanceId?.toString() ??
      alert?._id ??
      `${alert?.timestamp ?? ''}`
    );
  }

  private updateColumns(width: number): void {
    if (!width) return;
    this.columns.set(width <= NARROW_CONTAINER_WIDTH ? 1 : 2);
  }

  private updateListOffset(): void {
    const parent = this.scrollParent();
    const wrapper = this.listWrapper()?.nativeElement;
    if (!parent || !wrapper) return;
    this.listOffset.set(
      wrapper.getBoundingClientRect().top -
        parent.getBoundingClientRect().top +
        parent.scrollTop,
    );
  }

  private findScrollParent(element: HTMLElement | null): HTMLElement | null {
    let node = element?.parentElement ?? null;
    while (node) {
      const overflowY = getComputedStyle(node).overflowY;
      if (
        overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflowY === 'overlay'
      ) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  private fromLegacyResult(item: IntelligenceModels.Alerts.Item): AlertEntry {
    const edgeId = item.edgeId ?? '';
    const locationId = item.locationId ?? '';
    const category = this.parseNumber(item.category);
    return {
      _id: item.alertId,
      alertInstanceId: item.alertId,
      edgeId,
      cameraId: item.cameraId,
      locationId,
      timestamp: item.timestamp ?? 0,
      selectedCamera: { cameraId: item.cameraId, edgeId, locationId },
      alertType: AlertType.analytic,
      detectionType: DetectionType.Apperance,
      type: AnalyticClasses.person,
      mainThumbnail: this.thumbnailName(item.thumbnailUrl),
      mainThumbnailTimestamp: item.timestamp ?? 0,
      archived: item.archived ?? false,
      archivedAt: 0,
      eventId: item.eventId?.toString(),
      eventName: item.eventName,
      alertMessage: item.alertMessage,
      eventTags: item.eventTags ?? [],
      category,
      flowType: category === AlertCategory.System ? SystemType.Panic : undefined,
    };
  }

  private thumbnailName(thumbnailUrl?: string | null): string {
    return thumbnailUrl?.split('?')[0].split('/').pop() ?? '';
  }

  private parseNumber(value?: string): number | undefined {
    if (value === undefined || value === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}
